import { darken, styled } from "@mui/material/styles";
import type { ComponentProps } from "react";

import { Button as MuiButton } from "components/core/mui";

type AlignedButtonProps = { variant: "text"; aligned?: "left" | "right" };
type NonAlignedButtonProps = { variant?: "contained" | "outlined" };
type GradientButtonProps = { gradient?: boolean };

type Props = (AlignedButtonProps | NonAlignedButtonProps) &
  ComponentProps<typeof MuiButton> &
  GradientButtonProps;

/**
 * A tiny wrapper that allows us to add some more specific styles for our design.
 *
 * #### Styling
 *
 * The extra styling is actually applied in the theme, but without this wrapper we would end up with
 * invalid attributes being set on the HTML elements.
 *
 * #### Links
 *
 * - [https://mui.com/components/buttons](https://mui.com/components/buttons)
 */
const Button = styled(MuiButton, {
  name: "Button",
  shouldForwardProp: (prop) => prop !== "aligned" && prop !== "gradient",
})<Props>(({ gradient, disabled, loading, theme }) => ({
  ...(gradient &&
    !disabled &&
    !loading && {
      background: "linear-gradient(140deg, #2D805E 10%, #003932 100%);",
      "&:hover": {
        background: `linear-gradient(140deg, ${darken(theme.palette.green[100], 0.1)} 10%, ${darken(theme.palette.darkGreen, 0.1)} 100%);`,
      },
    }),
}));

export default Button;
